import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GenericInfoPanel } from '@/components';
import {
    CalendarBlankIcon,
    ClockIcon,
    FilmStripIcon,
    MapPinIcon,
    MonitorIcon,
    VideoIcon,
} from '@phosphor-icons/react';
import { SecurityCameraIconMirrored } from '@/utils/icons';
import { FullSource, SourceType } from '@/types/source';
import { formatVideoTime } from '@/utils/time';
import { formatDate } from '@/utils/date';
import { I18nNamespace } from '@/lib/i18n-namespaces';

interface SourceInfoPanelProps {
    source: FullSource;
}

export function SourceInfoPanel({ source }: SourceInfoPanelProps) {
    const { t } = useTranslation(I18nNamespace.SOURCES);

    const infoData = useMemo(() => {
        const isCamera = source.type === SourceType.CAMERA;
        const resolutionValue =
            isCamera && (!source.width || !source.height)
                ? '-'
                : `${source.width} × ${source.height}`;
        const fpsValue = isCamera && !source.fps ? '-' : `${source.fps} fps`;
        const durationValue = isCamera ? '-' : formatVideoTime(source.duration);

        return [
            {
                id: 'uploadDate',
                label: t('info.uploadDate'),
                value: formatDate(source.createdAt),
                icon: CalendarBlankIcon,
            },
            {
                id: 'lastModified',
                label: t('info.lastModified'),
                value: formatDate(source.updatedAt),
                icon: ClockIcon,
            },
            {
                id: 'sourceType',
                label: t('info.sourceType'),
                value: isCamera ? t('info.camera') : t('info.videoFile'),
                icon: isCamera ? SecurityCameraIconMirrored : VideoIcon,
            },
            {
                id: 'location',
                label: t('info.locationGroup'),
                value: t('info.unassigned'),
                icon: MapPinIcon,
            },
            {
                id: 'duration',
                label: t('info.duration'),
                value: durationValue,
                icon: ClockIcon,
            },
            {
                id: 'resolution',
                label: t('info.resolution'),
                value: resolutionValue,
                icon: MonitorIcon,
            },
            {
                id: 'fps',
                label: t('info.frameRate'),
                value: fpsValue,
                icon: FilmStripIcon,
            },
            ...(!isCamera && source.progress < 100
                ? [
                      {
                          id: 'analysisProgress',
                          label: t('info.analysisProgress'),
                          value: `${source.progress}%`,
                      },
                  ]
                : []),
        ];
    }, [source, t]);

    return (
        <GenericInfoPanel
            title={t('info.sourceDetails')}
            className="p-6"
            items={infoData}
        />
    );
}
